import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, XCircle, Trash2, RefreshCw, LogOut, Shield, Copy, Check, Mail } from "lucide-react";
import { useAuth } from "./AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const StatusPill = ({ status }) => {
  const map = {
    pending_approval: { c: "bg-amber-100 text-amber-800", l: "Wartend" },
    approved: { c: "bg-emerald-100 text-emerald-800", l: "Freigegeben" },
    rejected: { c: "bg-red-100 text-red-800", l: "Abgelehnt" },
  };
  const s = map[status] || { c: "bg-ink-100 text-ink-800", l: status };
  return <span className={`text-xs px-2 py-1 rounded-full font-medium ${s.c}`}>{s.l}</span>;
};

const Backoffice = () => {
  const { user, logout, authHeader } = useAuth();
  const [tab, setTab] = useState("gp"); // "gp" | "customers" | "finanzchecks"
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [filter, setFilter] = useState("all");
  const [openDetail, setOpenDetail] = useState(null); // customer detail modal
  const [detail, setDetail] = useState(null);
  const [openCheck, setOpenCheck] = useState(null); // finanzcheck detail
  const [copiedId, setCopiedId] = useState(""); // gp id whose link was copied

  const copyPartnerLink = async (gp) => {
    const code = gp.partner_code || gp.id;
    const link = `${window.location.origin}/finanzcheck/${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(gp.id);
      setTimeout(() => setCopiedId(""), 2000);
    } catch {}
  };

  const partnerLinkFor = (gp) =>
    `${window.location.origin}/finanzcheck/${gp.partner_code || gp.id}`;

  const shareMailFor = (gp) => {
    const link = partnerLinkFor(gp);
    const firstName = (gp.name || "").trim().split(/\s+/)[0] || "";
    const subject = "Kostenloser Vorsorge- & Finanzcheck – nur für dich";
    const body =
`Hallo,

hier ist der persönliche Vorsorge- & Finanzcheck von ${gp.name || firstName}:
${link}

14 kurze Fragen (ca. 3–5 Min.), sofort eine persönliche Erstanalyse per E-Mail mit CHF-Potenzialrechnung bis 65 und möglicher monatlicher Rente. Kostenlos und vertraulich.

Herzliche Grüsse
Moonshine Solutions GmbH`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareWhatsAppFor = (gp) => {
    const link = partnerLinkFor(gp);
    const text =
`Hey! 👋

Hier ist der persönliche Vorsorge- & Finanzcheck von ${gp.name || ""}:
${link}

14 kurze Fragen, sofort eine persönliche Erstanalyse per E-Mail (CHF-Potenzialrechnung bis 65 + mögliche Rente ab 65). Kostenlos & vertraulich.`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "gp") {
        const { data } = await axios.get(`${API}/auth/admin/users`, { headers: authHeader() });
        setUsers(data);
      } else if (tab === "customers") {
        const { data } = await axios.get(`${API}/auth/admin/customers`, { headers: authHeader() });
        setCustomers(data);
      } else if (tab === "finanzchecks") {
        const { data } = await axios.get(`${API}/finanzcheck/list`, { headers: authHeader() });
        setChecks(data.items || []);
      }
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [authHeader, tab]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    setOpenDetail(id); setDetail(null);
    try {
      const { data } = await axios.get(`${API}/auth/admin/customers/${id}`, { headers: authHeader() });
      setDetail(data);
    } catch (e) { console.error(e); }
  };

  const act = async (id, action) => {
    if (action === "delete" && !window.confirm("Wirklich löschen?")) return;
    setBusyId(id);
    try {
      if (action === "delete") {
        await axios.delete(`${API}/auth/admin/users/${id}`, { headers: authHeader() });
      } else {
        await axios.post(`${API}/auth/admin/users/${id}/${action}`, {}, { headers: authHeader() });
      }
      await load();
    } catch (e) {
      alert("Fehler: " + (e?.response?.data?.detail || e.message));
    } finally { setBusyId(""); }
  };

  const filtered = users.filter((u) => filter === "all" || u.status === filter);
  const pendingCount = users.filter((u) => u.status === "pending_approval").length;

  return (
    <div className="min-h-screen bg-ivory-50 text-ink-900">
      <header className="max-w-6xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="bo-logo-link">
          <img src="/moonshine-logo.png" alt="Moonshine Solutions" className="h-14 w-14 rounded-full object-cover ring-1 ring-gold-500/30" />
          <div className="leading-tight">
            <div className="font-display font-medium text-ink-900 text-[15px]">Moonshine</div>
            <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">Solutions</div>
          </div>
        </Link>
        <div className="flex items-center gap-4">
          <div className="hidden md:block text-sm text-ink-500">Eingeloggt als <strong>{user?.email}</strong></div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-gold-700 transition" data-testid="bo-logout">
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-700 mb-3">
            <Shield className="w-3.5 h-3.5" /> Backoffice
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-medium mb-2">Backoffice</h1>
          <p className="text-ink-700 mb-6">
            {tab === "gp" ? (
              <>Alle registrierten Geschäftspartner. {pendingCount > 0 && <span className="text-amber-700 font-medium">{pendingCount} Anfrage{pendingCount === 1 ? "" : "n"} warten auf Freigabe.</span>}</>
            ) : tab === "customers" ? (
              <>Alle erfassten Kunden inkl. Verträge und Ausweisbeglaubigungen.</>
            ) : (
              <>Alle ausgefüllten Finanzchecks inkl. versendeter Erstanalyse und zugewiesenem Partner.</>
            )}
          </p>

          {/* Main Tabs */}
          <div className="flex gap-2 mb-6 border-b border-ink-700/10">
            <button
              onClick={() => setTab("gp")}
              data-testid="tab-gp"
              className={`px-5 py-3 text-sm font-medium transition -mb-px border-b-2 ${tab === "gp" ? "border-gold-500 text-ink-900" : "border-transparent text-ink-500 hover:text-ink-900"}`}
            >Geschäftspartner{pendingCount > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">{pendingCount}</span>}</button>
            <button
              onClick={() => setTab("customers")}
              data-testid="tab-customers"
              className={`px-5 py-3 text-sm font-medium transition -mb-px border-b-2 ${tab === "customers" ? "border-gold-500 text-ink-900" : "border-transparent text-ink-500 hover:text-ink-900"}`}
            >Kunden</button>
            <button
              onClick={() => setTab("finanzchecks")}
              data-testid="tab-finanzchecks"
              className={`px-5 py-3 text-sm font-medium transition -mb-px border-b-2 ${tab === "finanzchecks" ? "border-gold-500 text-ink-900" : "border-transparent text-ink-500 hover:text-ink-900"}`}
            >Finanzchecks</button>
          </div>

          {tab === "gp" && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {[["all", "Alle"], ["pending_approval", "Wartend"], ["approved", "Freigegeben"], ["rejected", "Abgelehnt"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                data-testid={`filter-${v}`}
                className={`px-4 py-2 rounded-full text-sm transition ${filter === v ? "bg-ink-900 text-gold-300" : "bg-white border border-ink-700/10 text-ink-700 hover:border-gold-500"}`}
              >
                {l}
              </button>
            ))}
            <button onClick={load} className="ml-auto inline-flex items-center gap-2 text-sm text-ink-500 hover:text-gold-700" data-testid="bo-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
            </button>
          </div>
          )}

          <div className="rounded-2xl bg-white border border-ink-700/10 overflow-hidden">
            {tab === "gp" ? (
              loading ? (
                <div className="p-8 text-center text-ink-500 text-sm">Lade …</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-ink-500 text-sm">Keine Geschäftspartner in dieser Ansicht.</div>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ivory-100 text-xs uppercase tracking-wider text-ink-500">
                    <tr>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">E-Mail / GP-Nr</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Details</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Finanzcheck-Link</th>
                      <th className="text-right px-4 py-3">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => (
                      <tr key={u.id} className="border-t border-ink-700/5" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3 font-medium">
                          {u.name}
                          {u.status === "approved" && (
                            <div className="mt-2 flex items-center gap-1.5 flex-wrap md:hidden" data-testid={`mobile-actions-${u.id}`}>
                              <button
                                onClick={() => copyPartnerLink(u)}
                                data-testid={`mobile-copy-link-${u.id}`}
                                className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-1 transition ${copiedId === u.id ? "bg-emerald-600 text-white" : "bg-ivory-100 text-ink-700 border border-ink-700/10"}`}
                              >
                                {copiedId === u.id ? (<><Check className="w-3 h-3" /> Kopiert</>) : (<><Copy className="w-3 h-3" /> Link</>)}
                              </button>
                              <a
                                href={shareMailFor(u)}
                                className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-1 bg-ivory-100 text-ink-700 border border-ink-700/10"
                              >
                                <Mail className="w-3 h-3" /> Mail
                              </a>
                              <a
                                href={shareWhatsAppFor(u)}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-1 bg-[#25D366] text-white font-medium"
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.99-1.309A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>
                                </svg>
                                WA
                              </a>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div>{u.email}</div>
                          <div className="text-xs text-ink-500">GP-Nr: {u.gp_number || "—"}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-xs text-ink-500">
                          <div>{u.address}</div>
                          <div>*{u.birthdate}</div>
                        </td>
                        <td className="px-4 py-3"><StatusPill status={u.status} /></td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {u.status === "approved" ? (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <button
                                onClick={() => copyPartnerLink(u)}
                                data-testid={`copy-link-${u.id}`}
                                className={`inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1.5 transition ${copiedId === u.id ? "bg-emerald-600 text-white" : "bg-ivory-100 hover:bg-ink-900 hover:text-gold-300 text-ink-700 border border-ink-700/10"}`}
                                title={partnerLinkFor(u)}
                              >
                                {copiedId === u.id ? (<><Check className="w-3.5 h-3.5" /> Kopiert</>) : (<><Copy className="w-3.5 h-3.5" /> Link</>)}
                              </button>
                              <a
                                href={shareMailFor(u)}
                                data-testid={`mail-link-${u.id}`}
                                className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1.5 bg-ivory-100 hover:bg-ink-900 hover:text-gold-300 text-ink-700 border border-ink-700/10 transition"
                                title="Per E-Mail teilen"
                              >
                                <Mail className="w-3.5 h-3.5" /> Mail
                              </a>
                              <a
                                href={shareWhatsAppFor(u)}
                                target="_blank" rel="noopener noreferrer"
                                data-testid={`wa-link-${u.id}`}
                                className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1.5 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-medium transition"
                                title="Per WhatsApp teilen"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.99-1.309A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>
                                </svg>
                                WA
                              </a>
                            </div>
                          ) : (
                            <span className="text-xs text-ink-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {u.role !== "admin" && u.status === "pending_approval" && (
                              <>
                                <button onClick={() => act(u.id, "approve")} disabled={busyId === u.id} data-testid={`approve-${u.id}`} className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-3 py-1.5 rounded-full transition">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Freigeben
                                </button>
                                <button onClick={() => act(u.id, "reject")} disabled={busyId === u.id} data-testid={`reject-${u.id}`} className="inline-flex items-center gap-1 text-xs bg-white border border-red-200 hover:bg-red-50 text-red-700 px-3 py-1.5 rounded-full transition">
                                  <XCircle className="w-3.5 h-3.5" /> Ablehnen
                                </button>
                              </>
                            )}
                            {u.role !== "admin" && u.status === "rejected" && (
                              <button onClick={() => act(u.id, "approve")} disabled={busyId === u.id} data-testid={`reapprove-${u.id}`} className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 hover:bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full transition">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Doch freigeben
                              </button>
                            )}
                            {u.role !== "admin" && (
                              <button onClick={() => act(u.id, "delete")} disabled={busyId === u.id} data-testid={`delete-${u.id}`} className="text-ink-400 hover:text-red-600 p-1.5 transition">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )
            ) : tab === "customers" ? (
              // ─── Customers tab ───
              loading ? (
                <div className="p-8 text-center text-ink-500 text-sm">Lade …</div>
              ) : customers.length === 0 ? (
                <div className="p-8 text-center text-ink-500 text-sm">Noch keine Kunden erfasst.</div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ivory-100 text-xs uppercase tracking-wider text-ink-500">
                    <tr>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">E-Mail</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Vermittler</th>
                      <th className="text-center px-4 py-3">Verträge</th>
                      <th className="text-center px-4 py-3">Beglaubigungen</th>
                      <th className="text-right px-4 py-3">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.id} className="border-t border-ink-700/5" data-testid={`customer-row-${c.id}`}>
                        <td className="px-4 py-3 font-medium">{c.name || "—"}</td>
                        <td className="px-4 py-3">{c.email}</td>
                        <td className="px-4 py-3 hidden md:table-cell text-xs text-ink-500">{c.last_vermittler_email || "—"}</td>
                        <td className="px-4 py-3 text-center"><span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-ivory-100 text-ink-700 text-xs font-medium">{c.vertrag_count || 0}</span></td>
                        <td className="px-4 py-3 text-center"><span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-ivory-100 text-ink-700 text-xs font-medium">{c.cert_count || 0}</span></td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => loadDetail(c.id)} data-testid={`customer-detail-${c.id}`} className="text-xs bg-ink-900 hover:bg-ink-700 text-gold-300 px-3 py-1.5 rounded-full transition">
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )
            ) : (
              // ─── Finanzchecks tab ───
              loading ? (
                <div className="p-8 text-center text-ink-500 text-sm">Lade …</div>
              ) : checks.length === 0 ? (
                <div className="p-8 text-center text-ink-500 text-sm">Noch keine Finanzchecks eingegangen.</div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ivory-100 text-xs uppercase tracking-wider text-ink-500">
                    <tr>
                      <th className="text-left px-4 py-3">Datum</th>
                      <th className="text-left px-4 py-3">Kunde</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">E-Mail</th>
                      <th className="text-left px-4 py-3">Partner</th>
                      <th className="text-right px-4 py-3 hidden md:table-cell">Prognose 65</th>
                      <th className="text-right px-4 py-3">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map((c) => (
                      <tr key={c.id} className="border-t border-ink-700/5" data-testid={`fc-row-${c.id}`}>
                        <td className="px-4 py-3 text-xs text-ink-500 tabular-nums">{c.created_at?.slice(0,10)}</td>
                        <td className="px-4 py-3 font-medium">{c.customer_name}</td>
                        <td className="px-4 py-3 hidden md:table-cell text-xs text-ink-500">{c.customer_email}</td>
                        <td className="px-4 py-3 text-sm">{c.partner_name}</td>
                        <td className="px-4 py-3 hidden md:table-cell text-right text-sm tabular-nums text-gold-700 font-medium">CHF {new Intl.NumberFormat("de-CH").format(c.projection_65_chf || 0)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setOpenCheck(c)} data-testid={`fc-detail-${c.id}`} className="text-xs bg-ink-900 hover:bg-ink-700 text-gold-300 px-3 py-1.5 rounded-full transition">
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )
            )}
          </div>
        </motion.div>
      </main>

      {/* Customer detail modal */}
      {openDetail && (
        <div className="fixed inset-0 bg-ink-900/60 flex items-center justify-center z-50 p-4" onClick={() => setOpenDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            {!detail ? (
              <div className="text-sm text-ink-500">Lade …</div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-gold-700 mb-1">Kunde</div>
                    <h3 className="text-2xl font-display font-medium">{detail.customer.name}</h3>
                    <div className="text-sm text-ink-500">{detail.customer.email}</div>
                  </div>
                  <button onClick={() => setOpenDetail(null)} data-testid="customer-detail-close" className="text-ink-500 hover:text-ink-900">✕</button>
                </div>
                <div className="text-xs text-ink-500 mb-4 space-y-1">
                  {detail.customer.address && <div>{detail.customer.address}</div>}
                  {detail.customer.phone && <div>Tel.: {detail.customer.phone}</div>}
                  {detail.customer.birthdate && <div>Geb.: {detail.customer.birthdate}</div>}
                  <div>Betreut von: {detail.customer.last_vermittler_email || "—"}</div>
                </div>

                <div className="mt-6">
                  <div className="text-sm font-semibold mb-2">Verträge ({detail.vertraege.length})</div>
                  {detail.vertraege.length === 0 ? (
                    <div className="text-xs text-ink-500 italic">Keine Verträge</div>
                  ) : (
                    <ul className="space-y-2">
                      {detail.vertraege.map((v) => (
                        <li key={v.id} className="flex items-center justify-between text-sm bg-ivory-100 rounded-lg px-3 py-2">
                          <span className="truncate">{v.filename}</span>
                          <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/vertrag/download/${v.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-gold-700 hover:underline">PDF</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-4">
                  <div className="text-sm font-semibold mb-2">Ausweisbeglaubigungen ({detail.certs.length})</div>
                  {detail.certs.length === 0 ? (
                    <div className="text-xs text-ink-500 italic">Keine Beglaubigungen</div>
                  ) : (
                    <ul className="space-y-2">
                      {detail.certs.map((c) => (
                        <li key={c.id} className="flex items-center justify-between text-sm bg-ivory-100 rounded-lg px-3 py-2">
                          <span className="truncate">{c.filename}</span>
                          <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/id/download/${c.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-gold-700 hover:underline">PDF</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Finanzcheck detail modal */}
      {openCheck && (
        <div className="fixed inset-0 bg-ink-900/70 flex items-center justify-center z-50 p-4" onClick={() => setOpenCheck(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-gold-700 mb-1">Finanzcheck</div>
                <h3 className="text-2xl font-display font-medium">{openCheck.customer_name}</h3>
                <div className="text-sm text-ink-500">{openCheck.customer_email} · {openCheck.customer_phone}</div>
              </div>
              <button onClick={() => setOpenCheck(null)} data-testid="fc-detail-close" className="text-ink-500 hover:text-ink-900 text-xl">✕</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-6 text-sm">
              <div className="rounded-xl bg-ivory-100 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Partner</div>
                <div className="font-medium">{openCheck.partner_name}</div>
                <div className="text-xs text-ink-500">{openCheck.partner_email}</div>
              </div>
              <div className="rounded-xl bg-ink-900 text-ivory-100 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-gold-300 mb-1">Potenzial bis 65 · Rente ab 65</div>
                <div className="font-display text-xl text-gold-gradient font-medium tabular-nums">CHF {new Intl.NumberFormat("de-CH").format(openCheck.projection_65_chf || 0)}</div>
                <div className="text-xs text-ivory-100/70 mt-0.5">→ CHF {new Intl.NumberFormat("de-CH").format(openCheck.rente_max_chf || 0)} / Monat einkommenssteuerfrei</div>
              </div>
            </div>

            <div className="text-sm font-semibold mb-2 mt-2">Antworten</div>
            <div className="rounded-xl bg-ivory-100 divide-y divide-ink-700/5 text-sm mb-6">
              {Object.entries(openCheck.answers || {}).filter(([k]) => !k.startsWith("gp_")).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2 px-4 py-2">
                  <div className="text-ink-500 text-xs uppercase tracking-wide">{k}</div>
                  <div className="col-span-2 text-ink-900 break-words">
                    {Array.isArray(v) ? (v.length ? v.join(", ") : <em className="text-ink-500">–</em>) : v === "" || v === null ? <em className="text-ink-500">–</em> : String(v)}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-sm font-semibold mb-2">Versendete Erstanalyse (an Kunde, CC Partner)</div>
            <pre className="rounded-xl bg-ink-900 text-ivory-100 text-xs sm:text-sm p-4 whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-auto" data-testid="fc-analysis-body">
{openCheck.analysis_text || "(Keine Analyse verfügbar)"}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default Backoffice;
